import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuArrowLeft, LuCheck, LuPlus, LuSignature, LuTrash2 } from 'react-icons/lu'
import { CurrencySelect } from '@/frontend/components/marina/CurrencySelect'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { type LineItem, LineItemEditor } from '@/frontend/components/marina/LineItemEditor'
import { SignaturePad } from '@/frontend/components/marina/SignaturePad'
import { useBerths } from '@/frontend/hooks/useBerths'
import { useBoats } from '@/frontend/hooks/useBoats'
import { useCreateContract, useMarkPrepared } from '@/frontend/hooks/useContracts'
import { useCustomers } from '@/frontend/hooks/useCustomers'
import type { CurrencyCode } from '@/frontend/lib/currency'
import type { CreateContractInput } from '@/lib/marina/validators'

const FT_PER_METER = 3.28084

export const Route = createFileRoute('/dashboard/accounts/contracts/new')({
  component: NewContract,
})

function NewContract() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateContract()
  const markPrepared = useMarkPrepared()

  const CONTRACT_TYPES = [
    { value: 'WEEKLY', label: t('page.accounts.contracts.type_weekly') },
    { value: 'MONTHLY', label: t('page.accounts.contracts.type_monthly') },
    { value: 'SEASONAL', label: t('page.accounts.contracts.type_seasonal') },
    { value: 'ANNUAL', label: t('page.accounts.contracts.type_annual') },
  ]

  // Step 1 — Parties
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [boatId, setBoatId] = useState<string | null>(null)
  const [berthId, setBerthId] = useState<string | null>(null)

  // Step 2 — Details
  const [type, setType] = useState<'WEEKLY' | 'MONTHLY' | 'SEASONAL' | 'ANNUAL'>('MONTHLY')
  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(dayjs().add(1, 'month').format('YYYY-MM-DD'))
  const [depositIdr, setDepositIdr] = useState<number | null>(0)
  const [currency, setCurrency] = useState<CurrencyCode>('IDR')

  // Step 3 — Items
  const [items, setItems] = useState<LineItem[]>([{ description: 'Sewa Berth', quantity: 1, unitPriceIdr: 0 }])
  const [taxRate, setTaxRate] = useState(11)

  // Step 4 — Documents
  const [documentUrl, setDocumentUrl] = useState('')
  const [questions, setQuestions] = useState<{ id: number; q: string; a: string }[]>([])
  const [notes, setNotes] = useState('')
  const [signatureDataUrl, setSignatureDataUrl] = useState('')

  // Data
  const { data: customersData } = useCustomers({ pageSize: 200 })
  const { data: boatsData } = useBoats({ customerId: customerId ?? undefined, pageSize: 100 })
  const { data: berthsData } = useBerths({})

  const customers = customersData?.items ?? []
  const boats = boatsData?.items ?? []
  const berths = berthsData?.items ?? []

  const selectedCustomer = useMemo(() => customers.find((c) => c.id === customerId), [customers, customerId])
  const selectedBoat = useMemo(() => boats.find((b) => b.id === boatId), [boats, boatId])
  const selectedBerth = useMemo(() => berths.find((b) => b.id === berthId), [berths, berthId])

  const customerOptions = useMemo(
    () =>
      customers.map((c) => ({ value: c.id, label: `${c.firstName} ${c.lastName}${c.email ? ` · ${c.email}` : ''}` })),
    [customers],
  )
  const boatOptions = useMemo(() => boats.map((b) => ({ value: b.id, label: b.name })), [boats])
  const berthOptions = useMemo(
    () => berths.map((b) => ({ value: b.id, label: `${b.code}${b.pontoon?.name ? ` — ${b.pontoon.name}` : ''}` })),
    [berths],
  )

  const subtotal = items.reduce((sum, it) => sum + it.quantity * it.unitPriceIdr, 0)
  const tax = (subtotal * taxRate) / 100
  const total = subtotal + tax

  const durationDays = dayjs(endDate).diff(dayjs(startDate), 'day')
  const durationMonths = dayjs(endDate).diff(dayjs(startDate), 'month')
  const durationStr =
    durationMonths >= 1
      ? `${durationMonths} bulan${durationDays % 30 > 0 ? ` ${durationDays % 30} hari` : ''}`
      : `${durationDays} hari`
  const validDates = durationDays > 0

  const canSave = !!customerId && validDates && items.every((i) => i.description) && subtotal > 0

  const DATE_PRESETS = [
    { label: '1 minggu', apply: () => setEndDate(dayjs(startDate).add(1, 'week').format('YYYY-MM-DD')) },
    { label: '1 bulan', apply: () => setEndDate(dayjs(startDate).add(1, 'month').format('YYYY-MM-DD')) },
    { label: '3 bulan', apply: () => setEndDate(dayjs(startDate).add(3, 'month').format('YYYY-MM-DD')) },
    { label: '6 bulan', apply: () => setEndDate(dayjs(startDate).add(6, 'month').format('YYYY-MM-DD')) },
    { label: '1 tahun', apply: () => setEndDate(dayjs(startDate).add(1, 'year').format('YYYY-MM-DD')) },
  ]

  async function submit(action: 'draft' | 'prepared') {
    const payload: CreateContractInput = {
      customerId: customerId!,
      boatId,
      berthId,
      type,
      startDate,
      endDate,
      totalValueIdr: total,
      depositIdr: depositIdr ?? 0,
      currency,
      items: items.map((it) => ({ description: it.description, quantity: it.quantity, unitPriceIdr: it.unitPriceIdr })),
      documentUrl: signatureDataUrl || documentUrl || null,
      questions: questions.length > 0 ? questions.map(({ q, a }) => ({ q, a })) : null,
      notes: notes || null,
    }
    try {
      const { contract } = await create.mutateAsync(payload)
      if (action === 'prepared') await markPrepared.mutateAsync(contract.id)
      notifications.show({
        title: t('page.accounts.contracts.notify_created'),
        message:
          action === 'prepared'
            ? t('page.accounts.contracts.notify_prepared', { code: contract.code })
            : t('page.accounts.contracts.notify_draft', { code: contract.code }),
        color: 'teal',
      })
      navigate({ to: '/dashboard/accounts/contracts' })
    } catch (e: unknown) {
      notifications.show({
        title: t('common.error'),
        message: e instanceof Error ? e.message : t('common.try_again'),
        color: 'red',
      })
    }
  }

  return (
    <Container size="md" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Anchor component={Link} to="/dashboard/accounts/contracts" c="dimmed" style={{ display: 'flex' }}>
            <LuArrowLeft size={18} />
          </Anchor>
          <ThemeIcon variant="light" color="laut" size="lg" radius="md">
            <LuSignature size={18} />
          </ThemeIcon>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              {t('page.accounts.contracts.new_title')}
            </Title>
            <Text size="xs" c="dimmed">
              Isi semua bagian lalu simpan sebagai Draf atau tandai Siap
            </Text>
          </div>
        </Group>

        {/* Step 1 — Pelanggan */}
        <StepCard step={1} title="Pelanggan" done={!!customerId} required>
          <Select
            label="Pilih Pelanggan"
            required
            placeholder="Cari nama atau email pelanggan…"
            searchable
            clearable
            data={customerOptions}
            value={customerId}
            onChange={(v) => {
              setCustomerId(v)
              setBoatId(null)
            }}
          />
          {selectedCustomer && (
            <Box
              mt="xs"
              p="sm"
              style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))' }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={600}>
                    {selectedCustomer.firstName} {selectedCustomer.lastName}
                  </Text>
                  <Group gap="xs" mt={2} wrap="wrap">
                    <KindBadge category={selectedCustomer.category} />
                    {selectedCustomer.email && (
                      <Text size="xs" c="dimmed" truncate>
                        {selectedCustomer.email}
                      </Text>
                    )}
                    {selectedCustomer.phone && (
                      <Text size="xs" c="dimmed">
                        {selectedCustomer.phone}
                      </Text>
                    )}
                    {selectedCustomer._count && (
                      <Text size="xs" c="dimmed">
                        {selectedCustomer._count.bookings} booking · {selectedCustomer._count.contracts ?? 0} kontrak
                      </Text>
                    )}
                  </Group>
                </div>
              </Group>
            </Box>
          )}
          {!customerId && (
            <Text size="xs" c="dimmed" mt={4}>
              Belum terdaftar?{' '}
              <Anchor component={Link} to="/dashboard/accounts/customers/new" c="laut" size="xs">
                Tambah pelanggan baru →
              </Anchor>
            </Text>
          )}
        </StepCard>

        {/* Step 2 — Kapal (opsional) */}
        <StepCard step={2} title="Kapal" badge="Opsional" done={!!boatId} disabled={!customerId}>
          <Select
            label="Kapal Pelanggan"
            placeholder={
              !customerId ? 'Pilih pelanggan dulu' : boats.length === 0 ? 'Pelanggan belum punya kapal' : 'Pilih kapal…'
            }
            searchable
            clearable
            disabled={!customerId || boats.length === 0}
            data={boatOptions}
            value={boatId}
            onChange={setBoatId}
          />
          {selectedBoat && (
            <Box
              mt="xs"
              p="sm"
              style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))' }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={600}>
                    {selectedBoat.name}
                  </Text>
                  <Group gap="xs" mt={2} wrap="wrap">
                    <Badge size="xs" variant="light" color="blue">
                      {selectedBoat.type?.replace(/_/g, ' ')}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      LOA {(Number(selectedBoat.loaMeters) * FT_PER_METER).toFixed(1)} ft · Beam{' '}
                      {(Number(selectedBoat.beamMeters) * FT_PER_METER).toFixed(1)} ft
                    </Text>
                    {selectedBoat.flagState && (
                      <Text size="xs" c="dimmed">
                        {selectedBoat.flagState}
                      </Text>
                    )}
                  </Group>
                </div>
              </Group>
            </Box>
          )}
          {customerId && boats.length === 0 && (
            <Text size="xs" c="dimmed" mt={4}>
              Pelanggan ini belum punya kapal.{' '}
              <Anchor component={Link} to={`/dashboard/accounts/customers/${customerId}` as any} c="laut" size="xs">
                Tambah kapal →
              </Anchor>
            </Text>
          )}
        </StepCard>

        {/* Step 3 — Berth (opsional) */}
        <StepCard step={3} title="Berth" badge="Opsional" done={!!berthId}>
          <Select
            label="Pilih Berth"
            placeholder="Pilih berth yang akan dikontrak…"
            searchable
            clearable
            data={berthOptions}
            value={berthId}
            onChange={setBerthId}
          />
          {selectedBerth && (
            <Box
              mt="xs"
              p="sm"
              style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))' }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="xl">
                  <LuCheck size={12} />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={600}>
                    {selectedBerth.code}
                  </Text>
                  <Group gap="xs" mt={2} wrap="wrap">
                    <Text size="xs" c="dimmed">
                      Max LOA {Number(selectedBerth.maxLoaMeters).toFixed(1)} m · Beam{' '}
                      {Number(selectedBerth.maxBeamMeters).toFixed(1)} m · Dalam{' '}
                      {Number(selectedBerth.depthMeters).toFixed(1)} m
                    </Text>
                    <Text size="xs" c="laut.7" fw={500}>
                      · <CurrencyText idr={selectedBerth.dailyRateIdr} size="xs" span />
                      /hari
                    </Text>
                  </Group>
                </div>
              </Group>
            </Box>
          )}
        </StepCard>

        {/* Step 4 — Detail Kontrak */}
        <StepCard step={4} title="Detail Kontrak" done={validDates} required disabled={!customerId}>
          <Stack gap="md">
            <div>
              <Text size="sm" fw={500} mb={6}>
                Tipe Kontrak <span style={{ color: 'var(--mantine-color-red-6)' }}>*</span>
              </Text>
              <Group gap="xs" wrap="wrap">
                {CONTRACT_TYPES.map((ct) => (
                  <Button
                    key={ct.value}
                    size="xs"
                    variant={type === ct.value ? 'filled' : 'light'}
                    color="laut"
                    onClick={() => setType(ct.value as typeof type)}
                  >
                    {ct.label}
                  </Button>
                ))}
              </Group>
            </div>

            <div>
              <Text size="sm" fw={500} mb={6}>
                Periode Kontrak <span style={{ color: 'var(--mantine-color-red-6)' }}>*</span>
              </Text>
              <Group gap="xs" mb={8} wrap="wrap">
                {DATE_PRESETS.map((p) => (
                  <Button key={p.label} size="xs" variant="light" color="laut" onClick={p.apply}>
                    {p.label}
                  </Button>
                ))}
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Tanggal Mulai"
                  required
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.currentTarget.value)}
                />
                <TextInput
                  label="Tanggal Berakhir"
                  required
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.currentTarget.value)}
                />
              </SimpleGrid>
              {validDates ? (
                <Text size="xs" c="dimmed" mt={4}>
                  Durasi: <b>{durationStr}</b> · {dayjs(startDate).format('DD MMM YYYY')} –{' '}
                  {dayjs(endDate).format('DD MMM YYYY')}
                </Text>
              ) : endDate ? (
                <Text size="xs" c="red.6" mt={4}>
                  Tanggal berakhir harus setelah tanggal mulai.
                </Text>
              ) : null}
            </div>

            <Group gap="md" align="flex-end">
              <NumberInput
                label={`Deposit (${currency})`}
                description="Opsional — jumlah deposit yang wajib dibayar di awal kontrak"
                value={depositIdr ?? 0}
                onChange={(v) => setDepositIdr(typeof v === 'number' ? v : Number(v) || 0)}
                min={0}
                step={500_000}
                thousandSeparator=","
                maw={280}
              />
              <CurrencySelect
                label="Mata uang"
                description="Mata uang kontrak — semua nilai mengikuti pilihan ini"
                value={currency}
                onChange={setCurrency}
                w={170}
              />
            </Group>
          </Stack>
        </StepCard>

        {/* Step 5 — Item Kontrak */}
        <StepCard step={5} title="Item Kontrak" done={subtotal > 0} required disabled={!customerId}>
          <LineItemEditor items={items} onChange={setItems} taxRate={taxRate} onTaxRateChange={setTaxRate} />
          {subtotal <= 0 && (
            <Text size="xs" c="dimmed" mt="xs">
              Tambahkan minimal satu item dengan harga untuk bisa menyimpan kontrak.
            </Text>
          )}
        </StepCard>

        {/* Step 6 — Dokumen & Tanda Tangan */}
        <StepCard
          step={6}
          title="Dokumen & Tanda Tangan"
          badge="Opsional"
          done={!!(documentUrl || signatureDataUrl || notes || questions.length > 0)}
        >
          <Stack gap="md">
            <TextInput
              label="URL Dokumen"
              placeholder="https://drive.google.com/…"
              description="Tautan ke PDF kontrak yang telah ditandatangani (Google Drive, Dropbox, dll.)"
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.currentTarget.value)}
            />

            <div>
              <Group justify="space-between" mb={6}>
                <Text size="sm" fw={500}>
                  Pertanyaan / Persetujuan
                </Text>
                <Button
                  variant="light"
                  color="laut"
                  size="xs"
                  leftSection={<LuPlus size={13} />}
                  onClick={() => setQuestions([...questions, { id: Date.now(), q: '', a: '' }])}
                >
                  Tambah Pertanyaan
                </Button>
              </Group>
              {questions.length === 0 && (
                <Text size="xs" c="dimmed">
                  Kuesioner opsional — tambah pertanyaan dan catat jawaban persetujuan pelanggan.
                </Text>
              )}
              <Stack gap="xs">
                {questions.map((qa) => (
                  <Box
                    key={qa.id}
                    p="sm"
                    style={{
                      borderRadius: 8,
                      background: 'light-dark(var(--mantine-color-gray-0), rgba(255,255,255,0.04))',
                    }}
                  >
                    <Group gap="xs" align="flex-start" wrap="nowrap">
                      <Stack gap={4} style={{ flex: 1 }}>
                        <TextInput
                          size="xs"
                          placeholder="Pertanyaan"
                          value={qa.q}
                          onChange={(e) =>
                            setQuestions(
                              questions.map((x) => (x.id === qa.id ? { ...x, q: e.currentTarget.value } : x)),
                            )
                          }
                        />
                        <TextInput
                          size="xs"
                          placeholder="Jawaban pelanggan (opsional)"
                          value={qa.a}
                          onChange={(e) =>
                            setQuestions(
                              questions.map((x) => (x.id === qa.id ? { ...x, a: e.currentTarget.value } : x)),
                            )
                          }
                        />
                      </Stack>
                      <Button
                        variant="subtle"
                        color="red"
                        size="xs"
                        px={6}
                        onClick={() => setQuestions(questions.filter((x) => x.id !== qa.id))}
                      >
                        <LuTrash2 size={13} />
                      </Button>
                    </Group>
                  </Box>
                ))}
              </Stack>
            </div>

            <Textarea
              label="Catatan Internal"
              description="Hanya terlihat oleh staf — tidak ditampilkan ke pelanggan"
              rows={3}
              placeholder="Mis: catatan negosiasi, syarat khusus, referensi internal…"
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />

            <div>
              <Text size="sm" fw={500} mb={4}>
                Tanda Tangan Digital
              </Text>
              <Text size="xs" c="dimmed" mb="sm">
                Tanda tangan pelanggan opsional — disimpan sebagai gambar bersama dokumen kontrak.
              </Text>
              <SignaturePad value={signatureDataUrl} onChange={setSignatureDataUrl} />
            </div>
          </Stack>
        </StepCard>

        {/* Ringkasan + CTA */}
        <Card
          radius="md"
          padding="lg"
          style={{ background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.08))' }}
        >
          <Stack gap="sm">
            <Text fw={600} size="sm" c="laut.8">
              Ringkasan Kontrak
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
              <SummaryField
                label="Pelanggan"
                value={
                  selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : '— belum dipilih'
                }
              />
              <SummaryField label="Kapal" value={selectedBoat?.name ?? '—'} />
              <SummaryField label="Berth" value={selectedBerth?.code ?? '—'} />
              <SummaryField label="Tipe" value={CONTRACT_TYPES.find((ct) => ct.value === type)?.label ?? type} />
              <SummaryField label="Mulai" value={startDate ? dayjs(startDate).format('DD MMM YYYY') : '—'} />
              <SummaryField label="Berakhir" value={endDate ? dayjs(endDate).format('DD MMM YYYY') : '—'} />
              <SummaryField label="Durasi" value={validDates ? durationStr : '—'} />
              <SummaryField
                label="Deposit"
                value={depositIdr ? <CurrencyText idr={depositIdr} size="sm" fw={500} span /> : '—'}
              />
            </SimpleGrid>
            <Divider />
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
              <Group gap="xl">
                <div>
                  <Text size="xs" c="dimmed">
                    Subtotal
                  </Text>
                  <CurrencyText idr={subtotal} size="sm" />
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Pajak {taxRate}%
                  </Text>
                  <CurrencyText idr={tax} size="sm" />
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Total Nilai Kontrak
                  </Text>
                  <CurrencyText idr={total} fw={700} size="lg" c="laut.7" />
                </div>
              </Group>
              <Group gap="sm">
                <Button variant="default" onClick={() => navigate({ to: '/dashboard/accounts/contracts' })}>
                  {t('page.accounts.contracts.btn_cancel')}
                </Button>
                <Button
                  variant="light"
                  color="laut"
                  disabled={!canSave}
                  loading={create.isPending}
                  onClick={() => submit('draft')}
                >
                  {t('page.accounts.contracts.btn_draft')}
                </Button>
                <Button
                  color="karang"
                  disabled={!canSave}
                  loading={create.isPending || markPrepared.isPending}
                  onClick={() => submit('prepared')}
                >
                  {t('page.accounts.contracts.btn_prepared')}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  )
}

function StepCard({
  step,
  title,
  badge,
  done,
  disabled,
  required,
  children,
}: {
  step: number
  title: string
  badge?: string
  done: boolean
  disabled?: boolean
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <Card radius="md" padding="lg" style={{ opacity: disabled ? 0.5 : 1, transition: 'opacity 200ms' }}>
      <Group gap="sm" mb="md" align="center">
        <ThemeIcon
          size={26}
          radius="xl"
          color={done ? 'laut' : 'gray'}
          variant={done ? 'filled' : 'light'}
          style={{ flexShrink: 0 }}
        >
          {done ? (
            <LuCheck size={13} />
          ) : (
            <Text size="xs" fw={700}>
              {step}
            </Text>
          )}
        </ThemeIcon>
        <Text fw={600} size="sm">
          {title}
        </Text>
        {required && !done && (
          <Badge size="xs" color="red" variant="light">
            Wajib
          </Badge>
        )}
        {badge && !done && (
          <Badge size="xs" color="gray" variant="light">
            {badge}
          </Badge>
        )}
        {done && (
          <Badge size="xs" color="teal" variant="light">
            Selesai
          </Badge>
        )}
      </Group>
      <div style={{ pointerEvents: disabled ? 'none' : undefined }}>{children}</div>
    </Card>
  )
}

function SummaryField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500} truncate>
        {value}
      </Text>
    </div>
  )
}
